import { Bus, Circle, Power, Siren } from "lucide-react";

// Device states shown in the Device Health card (comparison, state bar and legend).
// `standardSec` is the state's interval from STANDARD_INTERVALS below (used by the vehicle popover);
// the Standard box uses the exact switch combination via getStandardInterval.
export const DEVICE_STATES = [
    {
        key: "running",
        label: "Running / ON",
        standardSec: 10,
        color: "#1aa054",
        tint: "#eafaf0",
        icon: Bus
    },
    {
        key: "engineOff",
        label: "Engine off, power on",
        standardSec: 3600,
        color: "#e08a1e",
        tint: "#fdf3e6",
        icon: Power
    },
    {
        key: "idle",
        label: "Idle / OFF",
        alertLabel: "Idle / powered off",
        standardSec: 60,
        color: "#8a97a3",
        tint: "#f1f3f5",
        icon: Circle
    },
    {
        key: "emergency",
        label: "Emergency ON",
        alertLabel: "Emergency alert",
        standardSec: 5,
        color: "#e5384d",
        tint: "#fdeef1",
        icon: Siren
    }
];

export const SWITCHES = [
    { key: "mains", label: "Mains" },
    { key: "ignition", label: "Ignition" },
    { key: "emergency", label: "Emergency", danger: true }
];

// The switch combination lives in GlobalFilter under the API's keys:
//   Mains = backupBatteryStatus (1 / 0), Ignition = ignitionOn (true / false), Emergency = emergencyStatus (1 / 0)
// Default: Mains ON, Ignition ON, Emergency OFF
export const DEFAULT_SWITCH_FILTER = { ignitionOn: true, emergencyStatus: 0, backupBatteryStatus: 1 };

// GlobalFilter -> { mains, ignition, emergency }
export const switchesFromFilter = (filter = {}) => ({
    mains: Number(filter.backupBatteryStatus) === 1,
    ignition: !!filter.ignitionOn,
    emergency: Number(filter.emergencyStatus) === 1
});

// { mains, ignition, emergency } -> GlobalFilter keys
export const filterFromSwitches = ({ mains, ignition, emergency }) => ({
    ignitionOn: !!ignition,
    emergencyStatus: emergency ? 1 : 0,
    backupBatteryStatus: mains ? 1 : 0
});

// Standard packet interval (sec) per switch combination — Mains / Ignition / Emergency.
// Mains OFF + Ignition ON is not a defined combination (no standard).
const STANDARD_INTERVALS = [
    { mains: true, ignition: true, emergency: false, standardSec: 10 },
    { mains: true, ignition: true, emergency: true, standardSec: 5 },
    { mains: true, ignition: false, emergency: true, standardSec: 5 },
    { mains: true, ignition: false, emergency: false, standardSec: 3600 },
    { mains: false, ignition: false, emergency: true, standardSec: 60 },
    { mains: false, ignition: false, emergency: false, standardSec: 60 }
];

// switch combination -> standard interval (sec), null when the combination has no standard
export const getStandardInterval = ({ mains, ignition, emergency }) => {
    const row = STANDARD_INTERVALS.find(
        (r) => r.mains === !!mains && r.ignition === !!ignition && r.emergency === !!emergency
    );
    return row ? row.standardSec : null;
};

export const getDeviceState = (key) => DEVICE_STATES.find((s) => s.key === key);

// switch combination -> device state shown in the alert row:
//   Emergency ON (any Mains / Ignition) -> Emergency alert
//   Mains ON  + Ignition ON             -> Running / ON
//   Mains ON  + Ignition OFF            -> Engine off, power on
//   Mains OFF (Ignition ON or OFF)      -> Idle / powered off
export const getStateForSwitches = ({ mains, ignition, emergency }) => {
    if (emergency) return getDeviceState("emergency");
    if (mains && ignition) return getDeviceState("running");
    if (mains) return getDeviceState("engineOff");
    return getDeviceState("idle");
};

// interval vs standard: "good" up to the standard, "bad" above it, "none" when either is missing.
// Compared on whole seconds, as shown by formatDuration, so the colour matches the number.
export const getIntervalStatus = (actual, standard) => {
    if (actual === null || actual === undefined || actual === "" || standard === null || standard === undefined) {
        return "none";
    }
    return Math.round(Number(actual)) > standard ? "bad" : "good";
};

// 10 -> "10s", 125 -> "2 min 5s", 3600 -> "1 h", 3700 -> "1 h 1 min"
export const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined || seconds === "") return "-";
    const s = Math.round(Number(seconds));
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)} min${s % 60 ? ` ${s % 60}s` : ""}`;
    const m = Math.floor((s % 3600) / 60);
    return `${Math.floor(s / 3600)} h${m ? ` ${m} min` : ""}`;
};
